/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React from "react";
import { useTranslation } from "react-i18next";

interface PreparationDataSectionProps {
  displayBatchId: string;
  batchDetails: any;
}

const PreparationDataSection: React.FC<PreparationDataSectionProps> = ({
  // displayBatchId,
  batchDetails,
}) => {
  const { t } = useTranslation();
  const powderConsumption = batchDetails?.powder_consumption?.[0];
  const solutionConsumption = batchDetails?.solution_consumption?.[0];

  const displayBatchId = powderConsumption?.powder_batch_number || "-";
  const powderProductName = powderConsumption?.media_product_name || "-";
  const powderLotCode = powderConsumption?.powder_lot_code || "-";
  const solutionProductName = solutionConsumption?.solution_product_name || "-";
  const solutionBatchNumber = solutionConsumption?.solution_batch_number || "-";
  const solutionLotCode = solutionConsumption?.solution_lot_code || "-";
  const actualValuePowder = powderConsumption?.quantity_used || '';
  const actualValueSolution = solutionConsumption?.quantity_used || '';

  const sterilization = batchDetails?.sterilization_details;
  const autoclaveId = sterilization?.autoclave_name || "-";
  const cycleType = sterilization?.cycle_type || "-";
  const sterilizationTemperature = sterilization?.max_temperature_observed || "-";
  const holdTime = sterilization?.hold_duration_min || "-";
  const startTime = sterilization?.cycle_start_datetime || "-";
  const status = sterilization?.steam_clox_indicator_result || "-";
  const targetTimeAttained = sterilization?.target_temp_attained_time || "-";
  
  const totalQuantityMl = Number(actualValueSolution || 0);
  const totalQuantityL = totalQuantityMl / 1000;

  const completedOn = (() => {
  if (!startTime || startTime === "-" || !holdTime) return "-";

  const [datePart, timePart] = startTime.split(" ");
  const [day, month, year] = datePart.split("-").map(Number);
  const [hour, minute, second] = timePart.split(":").map(Number);

  const completed = new Date(year, month - 1, day, hour, minute, second);
    completed.setMinutes(completed.getMinutes() + Number(holdTime));

    const dd = String(completed.getDate()).padStart(2, "0");
    const mm = String(completed.getMonth() + 1).padStart(2, "0");
    const yyyy = completed.getFullYear();
    const hh = String(completed.getHours()).padStart(2, "0");
    const min = String(completed.getMinutes()).padStart(2, "0");
    const sec = String(completed.getSeconds()).padStart(2, "0");

    return `${dd}-${mm}-${yyyy} ${hh}:${min}:${sec}`;
  })();

  return (
    <div className="space-y-4">
        {/* 3. Preparation Data */}
            <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-6 py-2 bg-[#F8FAFC] border-b border-slate-200 flex items-center gap-4 bg-white">
                <div className="w-6 h-6 rounded-sm bg-slate-900 text-white flex items-center justify-center font-black text-[10px]">03</div>
                <h3 className="text-[11.5px] font-black uppercase tracking-[0.08em] text-slate-900 mt-[7px]">{t("mediaPrepBatch.mediaPrepBatchDetails.PreparationData")}</h3>
              </div>
              <div className="p-2">
                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <table className="w-full text-left">
                  <thead>
                    <tr className="bg-slate-900 text-[9px] font-black text-white uppercase tracking-[0.2em] whitespace-nowrap">
                      <th className="px-8 py-3">{t("mediaPrepBatch.mediaPrepBatchDetails.Components")}</th>
                      <th className="px-8 py-3">{t("mediaPrepBatch.mediaPrepBatchDetails.BatchName")}</th>
                      <th className="px-8 py-3">{t("mediaPrepBatch.mediaPrepBatchDetails.LotName")}</th>
                      <th className="px-8 py-3 text-left">{t("mediaPrepBatch.mediaPrepBatchDetails.ActualValue")}</th>
                      <th className="px-8 py-3 text-right">{t("mediaPrepBatch.mediaPrepBatchDetails.unit")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 whitespace-nowrap">
                    <tr className="text-sm group hover:bg-slate-50 transition-colors">
                      <td className="px-8 py-3 font-black text-slate-900">{powderProductName}</td>
                      <td className="px-8 py-3 font-mono text-slate-500">{displayBatchId}</td>
                      <td className="px-8 py-3 font-mono text-slate-500">{powderLotCode}</td>
                      <td className="px-8 py-3 text-left font-black text-slate-900 bg-slate-50/50 group-hover:bg-orange-50 whitespace-nowrap">{actualValuePowder}</td>
                      <td className="px-8 py-3 text-center font-mono text-slate-500">gm</td>
                    </tr>
                    <tr className="text-sm group hover:bg-slate-50 transition-colors">
                      <td className="px-8 py-3 font-black text-slate-900">{solutionProductName}</td>
                      <td className="px-8 py-3 font-mono text-slate-500">{solutionBatchNumber}</td>
                      <td className="px-8 py-3 font-mono text-slate-500">{solutionLotCode}</td>
                      <td className="px-8 py-3 text-left font-black text-slate-900 bg-slate-50/50 group-hover:bg-orange-50 whitespace-nowrap">{actualValueSolution}</td>
                      <td className="px-8 py-3 text-center font-mono text-slate-500">ml</td>
                    </tr>
                    <tr className="border-t border-slate-200 bg-white">
                      <td colSpan={3} className="px-6 py-3 text-[14px] font-semibold text-slate-800">Total Quantity Prepared</td>
                      <td colSpan={2} className="px-6 py-3 text-right text-[14px] font-bold text-green-600 whitespace-nowrap">
                        {totalQuantityMl.toLocaleString()} mL ({totalQuantityL.toLocaleString()} L)
                      </td>
                    </tr>
                  </tbody>
                </table>
               </div> 
              </div>
            </section>

            {/* 4. Sterilization Record */}
            <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-6 py-2 border-b bg-[#F8FAFC] border-slate-200 flex items-center gap-4 bg-white"
                style={{borderBottom: "1px solid #e2e8f0",}}
              >
                <div className="w-6 h-6 rounded-sm bg-slate-900 text-white flex items-center justify-center font-black text-[10px]">04</div>
                <h3 className="text-[10.5px] font-black uppercase tracking-[0.08em] text-slate-900 mt-[7px]">{t("mediaPrepBatch.mediaPrepBatchDetails.SterilizationRecord")}</h3>
              </div>
              <div className="p-2 px-4 space-y-2">
                <div className="grid grid-cols-4 gap-6 px-2 py-2">
                  <div className="space-y-2">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t("mediaPrepBatch.mediaPrepBatchDetails.AutoclaveID")}</p>
                    <p className="text-sm font-black text-slate-900 font-mono">{autoclaveId}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t("mediaPrepBatch.mediaPrepBatchDetails.SterilizationTemp")}</p>
                    <p className="text-sm font-black text-slate-900 font-mono">{sterilizationTemperature} °C</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t("mediaPrepBatch.mediaPrepBatchDetails.HoldTime")}</p>
                    <p className="text-sm font-black text-slate-900 font-mono">{holdTime} min</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 p-2.5 bg-slate-900 text-white rounded-lg">
                  <div className="space-y-2">
                    <p className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em]">{t("mediaPrepBatch.mediaPrepBatchDetails.CycleStart")}</p>
                    <p className="text-xs font-black font-mono">{startTime}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em]">{t("mediaPrepBatch.mediaPrepBatchDetails.completedOn")}</p>
                    <p className="text-xs font-black font-mono">{completedOn}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em]">{t("mediaPrepBatch.mediaPrepBatchDetails.Duration")}</p>
                    <p className="text-xs font-black font-mono">{holdTime}</p>
                  </div>
                  <div className="flex flex-col">
                    <p className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] mb-2.5">{t("mediaPrepBatch.mediaPrepBatchDetails.status")}</p>
                    {status?.toUpperCase() === "PASS" ? (
                      <span className="inline-flex self-start items-center px-3 py-1 rounded-full bg-green-700 text-white text-[10px] font-bold uppercase">
                        {t("mediaPrepBatch.mediaPrepBatchDetails.completed")}
                      </span>
                    ) : (
                      <span className="text-xs font-black font-mono">
                        {status}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </section>
    </div>
    );
};
export default PreparationDataSection;